/** Admin: AI cost estimate (from recorded usage) and the real Alibaba Cloud bill. */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../api.ts';
import { useT, useUiLocale, type MsgKey } from '../i18n.ts';

type Feature = 'translate' | 'check' | 'summary' | 'image_text' | 'transcribe' | 'tts';
interface CostRow { feature: Feature; provider: string; model: string; calls: number; inputTokens: number; outputTokens: number; audioSeconds: number; chars: number; usd: number | null }
interface CostMonth { month: string; usd: number; unpriced: boolean; rows: CostRow[]; byOrganization: Array<{ organizationId: string | null; name: string | null; usd: number; calls: number }> }
interface Costs {
  estimate: { trackedSince: string | null; today: { usd: number; calls: number }; months: CostMonth[] };
  bill: {
    configured: boolean;
    error?: string;
    balance?: { available: number; currency: string } | null;
    months: Array<{ cycle: string; currency: string; total: number; items: Array<{ product: string; code: string; amount: number }> }>;
  };
}

export function CostsTab() {
  const t = useT();
  const ui = useUiLocale();
  const nf = ui === 'sv-SE' ? 'sv-SE' : ui === 'zh-Hans' ? 'zh-CN' : 'en-GB';
  const [data, setData] = useState<Costs | null>(null);
  const [failed, setFailed] = useState(false);
  const [month, setMonth] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (fresh = false) => {
    setBusy(true);
    try {
      setData(await api<Costs>('GET', `/api/admin/costs${fresh ? '?fresh=1' : ''}`));
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }, []);
  useEffect(() => void load(), [load]);

  if (failed && !data) return <p className="muted">{t('billError', { code: 'api' })}</p>;
  if (!data) return <p className="muted">…</p>;

  const n = (v: number) => Math.round(v).toLocaleString(nf);
  // Small amounts need more decimals to say anything (a day of chat is often a few US cents).
  const money = (v: number, cur = 'USD') => {
    const digits = Math.abs(v) >= 10 ? 2 : Math.abs(v) >= 0.1 ? 3 : 4;
    return `${cur === 'USD' ? '$' : `${cur} `}${v.toLocaleString(nf, { minimumFractionDigits: Math.min(2, digits), maximumFractionDigits: digits })}`;
  };
  const used = (r: CostRow) => {
    const parts: string[] = [];
    if (r.inputTokens || r.outputTokens) parts.push(`${n(r.inputTokens)} + ${n(r.outputTokens)} tokens`);
    if (r.audioSeconds) parts.push(`${(r.audioSeconds / 60).toLocaleString(nf, { maximumFractionDigits: 1 })} min`);
    if (!parts.length && r.chars) parts.push(`${n(r.chars)} ${t('costChars')}`);
    return parts.join(' · ') || '–';
  };

  const est = data.estimate;
  const thisMonth = new Date().toISOString().slice(0, 7);
  const current = est.months.find((m) => m.month === thisMonth);
  const shown = est.months.find((m) => m.month === (month ?? thisMonth)) ?? est.months[0];
  const bill = data.bill;

  return (
    <div className="stack">
      <h3>{t('costEstimateTitle')}</h3>
      <p className="muted small">{t('costEstimateNote')}</p>
      {!est.trackedSince ? (
        <p className="muted">{t('costNothingYet')}</p>
      ) : (
        <>
          <dl className="stats">
            <div><dt>{t('costToday')}</dt><dd>{money(est.today.usd)}</dd></div>
            <div><dt>{t('costThisMonth')}</dt><dd>{money(current?.usd ?? 0)}</dd></div>
            <div><dt>{t('costCalls')} ({t('costThisMonth').toLowerCase()})</dt><dd>{n(current?.rows.reduce((s, r) => s + r.calls, 0) ?? 0)}</dd></div>
          </dl>
          <p className="muted small">{t('costTrackedSince', { date: est.trackedSince })}</p>
          {est.months.length > 1 && (
            <div className="row">
              <select value={shown?.month ?? ''} onChange={(e) => setMonth(e.target.value)} aria-label={t('costThisMonth')}>
                {est.months.map((m) => (
                  <option key={m.month} value={m.month}>{m.month} · {money(m.usd)}</option>
                ))}
              </select>
            </div>
          )}
          {shown && (
            <>
              <h3>{t('costByFeature')}</h3>
              <div className="table-wrap">
                <table className="table small">
                  <tbody>
                    {shown.rows.map((r) => (
                      <tr key={`${r.feature}-${r.provider}-${r.model}`}>
                        <td>{t(`costF${r.feature}` as MsgKey)}<div className="muted">{r.model}</div></td>
                        <td className="num">{n(r.calls)}</td>
                        <td className="num">{used(r)}</td>
                        <td className="num">{r.usd === null ? '–' : money(r.usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {shown.unpriced && <p className="muted small">{t('costUnpriced')}</p>}
              {shown.byOrganization.length > 0 && (
                <>
                  <h3>{t('costByCompany')}</h3>
                  <table className="table small">
                    <tbody>
                      {shown.byOrganization.map((o) => (
                        <tr key={o.organizationId ?? 'none'}>
                          <td>{o.name ?? t('costUnknownOrg')}</td>
                          <td className="num">{n(o.calls)}</td>
                          <td className="num">{money(o.usd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </>
          )}
        </>
      )}

      <div className="row" style={{ justifyContent: 'space-between', marginTop: 12 }}>
        <h3>{t('billTitle')}</h3>
        {bill.configured && (
          <button className="link-btn" disabled={busy} onClick={() => void load(true)}>{t('billRefresh')}</button>
        )}
      </div>
      <p className="muted small">{t('billNote')}</p>
      {!bill.configured ? (
        <p className="small">{t('billNotConfigured')}</p>
      ) : bill.error ? (
        <p className="small">{bill.error === 'no_permission' ? t('billNoPermission') : t('billError', { code: bill.error })}</p>
      ) : (
        <>
          {bill.balance && (
            <dl className="stats">
              <div><dt>{t('billBalance')}</dt><dd>{money(bill.balance.available, bill.balance.currency || 'USD')}</dd></div>
            </dl>
          )}
          {bill.months.map((m) => (
            <div key={m.cycle} className="stack">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <strong>{m.cycle}</strong>
                <strong>{money(m.total, m.currency)}</strong>
              </div>
              {m.items.length === 0 ? (
                <p className="muted small">{t('billEmpty')}</p>
              ) : (
                <table className="table small">
                  <tbody>
                    {m.items.map((i) => (
                      <tr key={i.code}>
                        <td>{i.product}</td>
                        <td className="num">{money(i.amount, m.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
}
